import React, { useState, useEffect, useRef } from 'react';
import { MicrophoneIcon, ReloadIcon } from './icons';

// Add SpeechRecognition types to the window object for TypeScript
declare global {
    interface Window {
        SpeechRecognition: any;
        webkitSpeechRecognition: any;
    }
}

interface VoiceCaptureModalProps {
    isOpen: boolean;
    onClose: () => void;
    onAccept: (transcribedText: string) => void;
}

const VoiceCaptureModal: React.FC<VoiceCaptureModalProps> = ({ isOpen, onClose, onAccept }) => {
    const [isListening, setIsListening] = useState(false);
    const [transcript, setTranscript] = useState('');
    const [error, setError] = useState<string | null>(null);
    const recognitionRef = useRef<any | null>(null);

    useEffect(() => {
        if (isOpen) {
            // Reset state on open
            setTranscript('');
            setError(null);
            setIsListening(false);

            const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
            if (!SpeechRecognition) {
                setError('El reconocimiento de voz no es compatible con tu navegador. Prueba con Chrome o Edge.');
                return;
            }

            const recognition = new SpeechRecognition();
            recognition.continuous = true;
            recognition.interimResults = true;
            recognition.lang = 'es-ES';

            recognition.onstart = () => {
                setIsListening(true);
                setError(null);
            };

            recognition.onend = () => {
                setIsListening(false);
            };

            recognition.onerror = (event: any) => {
                if (event.error === 'no-speech' || event.error === 'audio-capture') {
                    setError('No se ha detectado voz. Asegúrate de que tu micrófono funciona y de que has dado permiso.');
                } else {
                    setError(`Error de reconocimiento: ${event.error}`);
                }
                setIsListening(false);
            };

            recognition.onresult = (event: any) => {
                let finalTranscript = '';
                for (let i = event.resultIndex; i < event.results.length; ++i) {
                    if (event.results[i].isFinal) {
                        finalTranscript += event.results[i][0].transcript;
                    }
                }
                if (finalTranscript) {
                    setTranscript(prev => prev.trim() + (prev.trim() ? ' ' : '') + finalTranscript.trim());
                }
            };
            
            recognitionRef.current = recognition;
            // Automatically start listening when the modal opens
            recognition.start();
        }

        // Cleanup on close
        return () => {
            if (recognitionRef.current) {
                recognitionRef.current.stop();
                recognitionRef.current = null;
            }
        };
    }, [isOpen]);

    const handleToggleListening = () => {
        if (!recognitionRef.current) return;
        if (isListening) {
            recognitionRef.current.stop();
        } else {
            setTranscript(''); // Clear previous transcript on new start
            recognitionRef.current.start();
        }
    };

    const handleAccept = () => {
        if (transcript.trim()) {
            onAccept(transcript.trim());
        }
    };
    
    if (!isOpen) return null;

    return (
        <div 
            className="fixed inset-0 bg-black bg-opacity-70 z-50 flex justify-center items-center"
            onClick={onClose}
        >
            <div 
                className="bg-light-surface dark:bg-surface rounded-lg shadow-xl p-8 w-[95%] sm:w-full max-w-lg border border-light-border dark:border-border flex flex-col items-center"
                onClick={e => e.stopPropagation()}
            >
                <h2 className="text-2xl font-bold text-light-text-primary dark:text-text-primary mb-4">Registro por Voz</h2>
                <p className="text-light-text-secondary dark:text-text-secondary mb-6 text-center">
                    {isListening ? 'Habla ahora. Di algo como "Gasto de 50 euros en Mercadona para Compras".' : 'Pulsa el micrófono para empezar a grabar.'}
                </p>

                <div 
                    onClick={handleToggleListening} 
                    className={`w-32 h-32 rounded-full flex items-center justify-center cursor-pointer transition-all duration-300 ${isListening ? 'bg-red-500 animate-pulse' : 'bg-primary hover:bg-blue-600'}`}
                >
                    <MicrophoneIcon className="w-16 h-16 text-white"/>
                </div>

                <div className="w-full min-h-[80px] mt-6 p-3 bg-gray-100 dark:bg-gray-800 rounded-lg text-center">
                    <p className="text-sm text-light-text-secondary dark:text-text-secondary italic">
                        {transcript || 'Transcripción aparecerá aquí...'}
                    </p>
                </div>
                {error && <p className="text-sm text-red-400 mt-2 text-center">{error}</p>}
                
                <div className="flex justify-center gap-4 mt-8 w-full">
                    <button 
                        onClick={handleToggleListening}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-light-text-primary dark:text-text-primary transition-colors font-semibold"
                    >
                        <ReloadIcon className="w-5 h-5"/>
                        Repetir
                    </button>
                    <button 
                        onClick={handleAccept} 
                        disabled={!transcript.trim()}
                        className="flex-1 px-4 py-2 rounded-md bg-secondary hover:bg-green-600 text-white font-semibold transition-colors disabled:bg-gray-500"
                    >
                        Aceptar
                    </button>
                </div>
            </div>
        </div>
    );
};

export default VoiceCaptureModal;